import type { ApiResponse } from '@gokrew/models/generic';
import type {
  CandidateReviewDetail,
  CandidateReviewListItem,
  CandidateReviewSummary,
  SaveCandidateOpinionPayload,
} from '@gokrew/models/recruiting-shares';
import api from './client';

/**
 * Candidates HR shared with me. Company and person come from the token, so
 * no id but the share's is ever sent.
 */
const endpoints = {
  base: '/employee/candidate-reviews',
  summary: '/employee/candidate-reviews/summary',
  one: (shareId: string) => `/employee/candidate-reviews/${shareId}`,
  resume: (shareId: string) => `/employee/candidate-reviews/${shareId}/resume`,
  opinion: (shareId: string) =>
    `/employee/candidate-reviews/${shareId}/opinion`,
};

/** The nav entry: how many shares I have and how many wait for me. */
export async function getCandidateReviewSummary() {
  const { data } = await api.get<ApiResponse<CandidateReviewSummary>>(
    endpoints.summary
  );
  return data.data;
}

export async function getCandidateReviews() {
  const { data } = await api.get<ApiResponse<CandidateReviewListItem[]>>(
    endpoints.base
  );
  return data.data;
}

/** One share; a 404 is shown by the page itself, never as a toast. */
export async function getCandidateReview(shareId: string) {
  const { data } = await api.get<ApiResponse<CandidateReviewDetail>>(
    endpoints.one(shareId),
    { quietStatuses: [404] }
  );
  return data.data;
}

/** The shared CV's short-lived link. */
export async function getCandidateReviewResume(shareId: string) {
  const { data } = await api.get<ApiResponse<{ url: string }>>(
    endpoints.resume(shareId),
    { quietStatuses: [404, 409] }
  );
  return data.data.url;
}

export async function saveCandidateOpinion(
  shareId: string,
  payload: SaveCandidateOpinionPayload
) {
  const { data } = await api.put<ApiResponse<CandidateReviewDetail>>(
    endpoints.opinion(shareId),
    payload
  );
  return data.data;
}
